import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

// ── Task states ──────────────────────────────────────────────────────────────
// The generic task status uses raw Celery states (note FAILURE, not FAILED). They are kept as they come; an unknown
// state is shown as it is.
export const TERMINAL_STATES = ["SUCCESS", "FAILURE", "REVOKED"];
export const isTerminalState = (state) => TERMINAL_STATES.includes(state);
export const isActiveState = (state) => !isTerminalState(state) && state !== "ERROR";

export const STATE_LABEL = {
  PENDING: "Waiting for a worker",
  RECEIVED: "Received",
  STARTED: "Running",
  RETRY: "Retrying",
  SUCCESS: "Finished",
  FAILURE: "Failed",
  REVOKED: "Revoked",
  ERROR: "Status unavailable",
};

// What the finished task actually did. SUCCESS only means the worker completed it: the result can say it skipped the
// work, reported a problem, or only dispatched follow-up jobs. The result is task-specific, so it is read defensively.
export const taskOutcome = (task) => {
  const state = task?.state;
  if (state === "FAILURE") return { kind: "failed", label: task.error || "Failed" };
  if (state === "REVOKED") return { kind: "revoked", label: "Revoked" };
  if (state === "ERROR") return { kind: "unknown", label: "Status unavailable" };
  if (state !== "SUCCESS") return { kind: "running", label: "" };
  const result = task.result;
  if (result && typeof result === "object") {
    if (result.skipped === true) return { kind: "skipped", label: "Skipped: ran recently" };
    if (result.success === false) return { kind: "problem", label: "Reported a problem" };
    if (result.pipeline_dispatched === true) return { kind: "dispatched", label: "Dispatched" };
  }
  // The catalogue marks dispatchers: their SUCCESS only means the work was handed out.
  if (task.completion === "dispatcher_only") return { kind: "dispatched", label: "Dispatched" };
  return { kind: "completed", label: "Completed" };
};

// Organization verification has its own lowercase states and a stats block.
export const verifyOrgToTaskState = (status) =>
  ({ accepted: "PENDING", already_running: "STARTED", starting: "STARTED", complete: "SUCCESS", error: "FAILURE" }[status] ||
  (status === "never_run" ? "PENDING" : String(status || "PENDING").toUpperCase()));

// ── Errors ───────────────────────────────────────────────────────────────────
// `detail` can be a string, an object ({ error, available_tasks, suggestion }) or a validation array. Everything is
// turned into plain text (never "[object Object]"), plus per-field messages when the API points at a field.
export const normalizeApiError = (error) => {
  const status = error?.status;
  if (status === "FETCH_ERROR" || status === "TIMEOUT_ERROR" || status === "PARSING_ERROR") {
    return {
      status,
      message: "Request outcome unknown. Check the task tracker before trying again.",
      fieldErrors: {},
      uncertain: true,
    };
  }
  const detail = error?.data?.detail ?? error?.data?.message ?? (typeof error?.data === "string" ? error.data : undefined);
  const fieldErrors = {};
  let message = "";
  if (Array.isArray(detail)) {
    message = detail
      .map((item) => {
        const loc = Array.isArray(item?.loc) ? item.loc.filter((part) => part !== "query" && part !== "body") : [];
        const field = loc.join(".");
        const text = String(item?.msg ?? item?.message ?? "Invalid value");
        if (field) fieldErrors[field] = text;
        return field ? `${field}: ${text}` : text;
      })
      .join("; ");
  } else if (detail && typeof detail === "object") {
    message = [detail.error, detail.suggestion].filter(Boolean).join(". ") || "The request was rejected.";
  } else if (typeof detail === "string") {
    message = detail;
  }
  if (!message) {
    message =
      {
        401: "Your session has expired. Sign in again.",
        403: "You don't have permission to do this.",
        404: "Not found.",
        406: "The sync service refused the request.",
        409: "That already exists.",
        422: "Some values were not accepted.",
        503: "The sync service isn't ready. Try again in a moment.",
      }[status] || (status >= 500 ? "The sync service hit an error." : "The request failed.");
  }
  return { status, message, fieldErrors, uncertain: false };
};

// ── Time ─────────────────────────────────────────────────────────────────────
// Backend timestamps can be naive UTC (no trailing Z). They are read as UTC, never as browser-local time.
export const parseBackendTime = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number") return dayjs(value);
  const text = String(value);
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(text);
  const parsed = hasZone ? dayjs(text) : dayjs.utc(text);
  return parsed.isValid() ? parsed.local() : null;
};

// The short zone name for a moment, the way people say it: IST, EDT, PST. The browser reports India as "GMT+5:30", so
// that one is named here; any other zone with no short name falls back to "GMT+X".
export const timeZoneAbbreviation = (date = new Date(), timeZone) => {
  const name = new Intl.DateTimeFormat("en-US", { timeZoneName: "short", timeZone }).formatToParts(date).find((part) => part.type === "timeZoneName")?.value || "";
  if (name === "GMT+5:30") return "IST";
  return name;
};

// A date and time in the viewer's zone, with the zone named so there is no guessing. `timeZone` (an IANA name such as
// "Asia/Kolkata") shows it in that zone instead; the page never passes one, so it is for tests that must not depend on
// the zone of the machine they run on.
export const formatDateTime = (value, timeZone) => {
  const time = parseBackendTime(value);
  if (!time) return "—";
  const shown = timeZone ? time.tz(timeZone) : time;
  return `${shown.format("MMM D, YYYY h:mm A")} ${timeZoneAbbreviation(time.toDate(), timeZone)}`;
};

export const timeAgo = (value, now = Date.now()) => {
  const time = parseBackendTime(value);
  if (!time) return "—";
  const minutes = Math.floor((now - time.valueOf()) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
};

// ── Schedules ────────────────────────────────────────────────────────────────
export const INTERVAL_PERIODS = ["seconds", "minutes", "hours", "days"];

export const scheduleWhen = (schedule) => {
  if (schedule?.schedule_type === "interval" && schedule.interval) {
    const { every, period } = schedule.interval;
    return `Every ${every} ${every === 1 ? String(period).replace(/s$/, "") : period}`;
  }
  const cron = schedule?.crontab;
  if (cron) {
    return [cron.minute, cron.hour, cron.day_of_month, cron.month_of_year, cron.day_of_week].map((part) => part ?? "*").join(" ");
  }
  return "—";
};
